import React, {useCallback, useState} from 'react';
import {
  Typography,
  Box,
  Link,
  Grid,
  useTheme,
  Tab,
  Tabs,
  Badge,
  Button,
  useMediaQuery
} from '@mui/material';
import {
  AccessTime,
  LocationOn,
  Person,
  Groups,
  Repeat,
  OndemandVideo
} from '@mui/icons-material';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import {AGE_GROUPS} from '@/constants';
import {useTranslation} from 'react-i18next';
import {formatDate, formatRecurring, useTitleStyle} from '@/utils/';
import {EventData} from '@/types/event';
import {Sport} from '@/types/common';
import {useAppSelector} from '@/redux-store/store';
import {CardMediaComponent} from '@/components/ui/card-media';
import Linkify from 'linkify-react';
import {fetchOtherUserData} from '@/rest/user/fetch-other-user-data';
import UserOverview, {UserInOverview} from '@/components/user/overview';
import LockIcon from '@mui/icons-material/Lock';
import {env} from '@/config';
import Chat from '@/components/matrix-chat';
import NoMemberInfo from '@/components/matrix-chat/ui/no-member-info';
import ResponsiveTypography from '@/components/ui/responsive-typography';
import EuroIcon from '@mui/icons-material/Euro';
import {useMatrixUnreadEventsContext} from '@/contexts/matrix/unread-events';
import {isBrowserSafari} from '@/services/matrix/utils';
import SafariMatrixInfo from '@/components/matrix-chat/ui/safari-matrix-info';
import DocumentShare from '@/components/ui/document-share';
import SportsBasketballIcon from '@mui/icons-material/SportsBasketball';
import JitsiButton from '@/components/jitsi/jitsi-button';
import qrCodeSportJoe from '@/assets/images/sportjoe-link-qr.jpg';
import {isEventInPast} from '@/services/events';

interface EventInfoProps {
  selectedEvent: EventData;
  isUserAuthorizedToSeeRoom: boolean;
  onUserOverviewOpenChange: (isOpen: boolean) => void;
  onGroupWindowOpen: (groupId: string) => void;
  formattedPrice: string;
  isUserParticipant: boolean;
  isUserAdmin?: boolean;
  onEditButtonClick?: () => void;
  onCloneButtonClick?: () => void;
  onDeleteButtonClick?: () => void;
  defaultChatTab?: boolean;
  trainerInSjOne?: boolean;
  isUserManager?: boolean;
  companyName?: string;
}

const EventInfo: React.FC<EventInfoProps> = ({
  selectedEvent,
  isUserAuthorizedToSeeRoom,
  onUserOverviewOpenChange,
  defaultChatTab = false,
  formattedPrice,
  onGroupWindowOpen,
  isUserParticipant,
  isUserAdmin,
  trainerInSjOne,
  isUserManager,
  companyName,
  onEditButtonClick,
  onCloneButtonClick,
  onDeleteButtonClick
}) => {
  const {t, i18n} = useTranslation();

  const theme = useTheme();

  const isSmallerScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const {unreadEventsCount} = useMatrixUnreadEventsContext();

  const isSafari = isBrowserSafari();

  const pastEvent = isEventInPast(selectedEvent);

  const {sports} = useAppSelector((state) => state.sports);

  const {CONTENT_SERVER} = env;

  const [userOverviewDialogOpen, setUserOverviewDialogOpen] = useState(false);
  const [selectedUserToOverview, setSelectedUserToOverview] =
    useState<UserInOverview | null>(null);
  const [tabValue, setTabValue] = useState(defaultChatTab ? 1 : 0);

  const createdByStyle = useTitleStyle('26ch', undefined, 'md');

  const trainerInSjOneNoManager = trainerInSjOne && !isUserManager;

  const hostedByText = trainerInSjOneNoManager
    ? `${t('hosted_for')} ${companyName}`
    : selectedEvent.onDemand
    ? `${t('hosted_by')} SportJoe`
    : `${t('hosted_by')} ${selectedEvent.createdBy?.name || t('deleted_user')}`;

  const eventCancelled = selectedEvent.status === 'CANCELLED';

  const handleTabChange = useCallback(
    (_: React.ChangeEvent<unknown>, newValue: number) => {
      setTabValue(newValue);
    },
    []
  );

  const handleUserOverviewClick = useCallback(
    async (userId: string) => {
      try {
        if (!trainerInSjOneNoManager) {
          const fetchedUser = await fetchOtherUserData(userId);
          setSelectedUserToOverview(fetchedUser);
          setUserOverviewDialogOpen(true);
          onUserOverviewOpenChange(true);
        }
      } catch (error) {
        console.error('Failed to fetch user', error);
        throw error;
      }
    },
    [onUserOverviewOpenChange, trainerInSjOneNoManager]
  );

  const handleUserOverviewClose = useCallback(() => {
    setSelectedUserToOverview(null);
    setUserOverviewDialogOpen(false);
    onUserOverviewOpenChange(false);
  }, [onUserOverviewOpenChange]);

  const handleFetchGroupData = useCallback(async () => {
    if (selectedEvent.group && selectedEvent.group._id) {
      onGroupWindowOpen(selectedEvent.group._id);
    }
  }, [onGroupWindowOpen, selectedEvent.group]);

  const handleGoToEvent = useCallback(() => {
    if (selectedEvent.onDemand && selectedEvent.onDemandLink) {
      window.open(selectedEvent.onDemandLink, '_blank');
    }
  }, [selectedEvent.onDemand, selectedEvent.onDemandLink]);

  const sportData: Sport | null = (() => {
    const sport = selectedEvent?.sports[0].sport;

    if (typeof sport === 'string') {
      return sports[sport];
    } else if (typeof sport === 'object') {
      return sport;
    }
    return null;
  })();

  if (!sportData) {
    return null;
  }

  const sportImageUrl = sportData.picture.startsWith('http')
    ? sportData.picture
    : CONTENT_SERVER + sportData.picture;

  const imageUrl = selectedEvent.picture || sportImageUrl;

  return (
    <Grid container spacing={2} direction={{xs: 'column', sm: 'row'}}>
      <Grid item xs={12}>
        {sportData && (
          <CardMediaComponent
            image={imageUrl || ''}
            altText={
              i18n.language === 'en' ? sportData.labels.en : sportData.labels.de
            }
          />
        )}
      </Grid>
      <Grid
        container
        item
        xs={12}
        spacing={2}
        direction={{xs: 'column', sm: 'row'}}
      >
        {!selectedEvent.onDemand && (
          <Grid item xs={12}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              centered
              TabIndicatorProps={{
                style: {backgroundColor: theme.palette.text.primary}
              }}
            >
              <Tab
                label={
                  <ResponsiveTypography
                    sx={{
                      color: theme.palette.text.primary,
                      paddingLeft: {xs: 1, sm: 4},
                      paddingRight: {xs: 1, sm: 4}
                    }}
                  >
                    {t('event_details')}
                  </ResponsiveTypography>
                }
              />
              {!trainerInSjOneNoManager && (
                <Tab
                  label={
                    <Badge
                      badgeContent={unreadEventsCount}
                      color="primary"
                      anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right'
                      }}
                    >
                      <ResponsiveTypography
                        sx={{
                          color: theme.palette.text.primary,
                          paddingLeft: 4,
                          paddingRight: 4
                        }}
                      >
                        {t('chat')}
                      </ResponsiveTypography>
                    </Badge>
                  }
                />
              )}
            </Tabs>
          </Grid>
        )}

        {tabValue === 0 && (
          <>
            <Grid
              container
              justifyContent={'space-between'}
              alignItems={'flex-start'}
            >
              <Grid item xs={12} sm={selectedEvent.onDemand ? 8 : 12}>
                <Box marginBottom={1} marginTop={2}>
                  <ResponsiveTypography
                    smallerXs
                    truncateText
                    sx={{marginLeft: 0.5}}
                  >
                    {i18n.language === 'en'
                      ? sportData.labels.en
                      : sportData.labels.de}
                  </ResponsiveTypography>
                </Box>
                <ResponsiveTypography
                  medium
                  truncateText
                  sx={{marginLeft: 0.5}}
                >
                  {selectedEvent.title}
                  {eventCancelled ? ` (${t('cancelled')})` : ''}
                  {pastEvent && !eventCancelled ? ` (${t('past_event')})` : ''}
                </ResponsiveTypography>

                <Box
                  marginBottom={2}
                  marginTop={1}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    gap: 2
                  }}
                >
                  <Grid xs={12}>
                    <Typography
                      variant="body1"
                      style={{whiteSpace: 'pre-line'}}
                      marginLeft={0.5}
                    >
                      <Linkify
                        options={{
                          target: '_blank',
                          rel: 'noopener noreferrer',
                          attributes: {
                            style: {
                              color: theme.palette.customColors?.urlLink
                            }
                          }
                        }}
                      >
                        {selectedEvent.description}
                      </Linkify>
                    </Typography>
                  </Grid>
                </Box>

                <Box mt={2}>
                  {!selectedEvent.onDemand && (
                    <Typography variant="body1" marginTop={2} marginLeft={0.5}>
                      <AccessTime
                        fontSize="inherit"
                        sx={{marginRight: '8px'}}
                      />{' '}
                      {formatDate(new Date(selectedEvent.start), [])}
                    </Typography>
                  )}

                  {!selectedEvent.online && (
                    <Typography variant="body1" marginTop={2} marginLeft={0.5}>
                      <Link
                        href={`https://www.google.com/maps/search/?api=1&query=${selectedEvent.locationName}`}
                        target="_blank"
                        style={{
                          textDecoration: 'none',
                          color: 'inherit'
                        }}
                      >
                        <LocationOn
                          fontSize="inherit"
                          sx={{marginRight: '12px'}}
                        />
                        {selectedEvent.locationName}
                      </Link>
                    </Typography>
                  )}

                  <Typography
                    variant="body1"
                    marginTop={2}
                    marginLeft={0.5}
                    sx={{
                      ...createdByStyle,
                      cursor: trainerInSjOneNoManager ? 'default' : 'pointer'
                    }}
                    onClick={() =>
                      handleUserOverviewClick(selectedEvent.createdBy?._id)
                    }
                  >
                    <Person fontSize="inherit" sx={{marginRight: '8px'}} />{' '}
                    {hostedByText}
                    {!trainerInSjOneNoManager && (
                      <Linkify
                        options={{
                          target: '_blank',
                          rel: 'noopener noreferrer',
                          attributes: {
                            style: {
                              color: theme.palette.customColors?.urlLink
                            },
                            onClick: (e: React.MouseEvent) =>
                              e.stopPropagation()
                          },
                          format: (value) =>
                            selectedEvent.isPaid &&
                            selectedEvent.imprintLink === value
                              ? t('imprint')
                              : value
                        }}
                      >
                        {selectedEvent.isPaid && selectedEvent.imprintLink
                          ? `: ${selectedEvent.imprintLink}`
                          : ''}
                      </Linkify>
                    )}
                  </Typography>
                  {selectedEvent.group && (
                    <Typography
                      onClick={() => {
                        handleFetchGroupData();
                      }}
                      variant="body1"
                      marginTop={2}
                      marginLeft={0.5}
                      sx={{
                        cursor: 'pointer'
                      }}
                    >
                      <Groups fontSize="inherit" sx={{marginRight: '8px'}} />{' '}
                      {selectedEvent.group.title}
                    </Typography>
                  )}
                  {selectedEvent.recurring &&
                    selectedEvent.recurring.length > 0 && (
                      <Typography
                        variant="body1"
                        marginTop={2}
                        marginLeft={0.5}
                      >
                        <Repeat fontSize="inherit" sx={{marginRight: '8px'}} />{' '}
                        {formatRecurring(selectedEvent.recurring)}
                      </Typography>
                    )}
                  <Box
                    sx={{
                      display: {
                        xs: 'block',
                        sm: 'none'
                      }
                    }}
                  >
                    {selectedEvent.online && (
                      <Typography
                        variant="body1"
                        marginTop={2}
                        marginLeft={0.5}
                      >
                        <OndemandVideo
                          fontSize="inherit"
                          sx={{marginRight: '12px'}}
                        />
                        {t('online_event')}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body1" marginTop={2} marginLeft={0.5}>
                    <PeopleAltIcon
                      fontSize="inherit"
                      sx={{marginRight: '8px'}}
                    />{' '}
                    {selectedEvent.ageGroups &&
                    selectedEvent.ageGroups.length > 0
                      ? selectedEvent.ageGroups
                          .map((ageGroup) => {
                            const foundGroup = AGE_GROUPS.find(
                              (group) => group.value === ageGroup
                            );
                            return foundGroup ? t(foundGroup.labelKey) : '';
                          })
                          .join(', ')
                      : t('ageGroup_all')}
                  </Typography>
                  {selectedEvent.trainerEvent && (
                    <Typography variant="body1" marginTop={2} marginLeft={0.5}>
                      <SportsBasketballIcon
                        fontSize="inherit"
                        sx={{marginRight: '12px'}}
                      />
                      {t('trainer_event')}
                    </Typography>
                  )}
                  {selectedEvent.isPaid && formattedPrice && (
                    <Typography variant="body1" marginTop={2} marginLeft={0.5}>
                      <EuroIcon fontSize="inherit" sx={{marginRight: '12px'}} />
                      {`${t('paid_event')}: ${formattedPrice}€${
                        selectedEvent.recurring.length > 0
                          ? ' ' + t('for_recurring_events')
                          : ''
                      }`}
                    </Typography>
                  )}
                  {selectedEvent.accessType &&
                    selectedEvent.accessType === 'PRIVATE' && (
                      <Typography
                        variant="body1"
                        marginTop={2}
                        marginLeft={0.5}
                      >
                        <LockIcon
                          fontSize="inherit"
                          sx={{marginRight: '8px'}}
                        />{' '}
                        {t('private_title')}
                      </Typography>
                    )}
                  {!trainerInSjOneNoManager && (
                    <DocumentShare
                      document={selectedEvent}
                      documentType={
                        selectedEvent.onDemand ? 'onDemandEvent' : 'event'
                      }
                    />
                  )}
                  {selectedEvent.online &&
                    !selectedEvent.onDemand &&
                    isUserParticipant && <JitsiButton event={selectedEvent} />}
                </Box>
              </Grid>
              {selectedEvent.onDemand && !isSmallerScreen && (
                <Grid
                  xs={12}
                  sm={4}
                  sx={{
                    marginTop: {xs: 0, sm: 6}
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 3
                    }}
                  >
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={handleGoToEvent}
                      sx={{
                        width: '100%'
                      }}
                    >
                      {t('go_to_event')}
                    </Button>
                    {isUserAdmin && (
                      <>
                        <Button
                          variant="contained"
                          color="secondary"
                          onClick={onEditButtonClick}
                          sx={{
                            width: '100%'
                          }}
                        >
                          {t('edit')}
                        </Button>

                        <Button
                          variant="contained"
                          color="secondary"
                          onClick={onCloneButtonClick}
                          sx={{
                            width: '100%'
                          }}
                        >
                          {t('clone')}
                        </Button>

                        <Button
                          variant="contained"
                          color="secondary"
                          onClick={onDeleteButtonClick}
                          sx={{
                            width: '100%'
                          }}
                        >
                          {t('delete')}
                        </Button>
                      </>
                    )}
                    <Box
                      sx={{
                        marginTop: 1,
                        width: '85%',
                        height: '85%',
                        flexDirection: 'column',
                        display: 'flex',
                        alignItems: 'center',
                        mx: 'auto'
                      }}
                    >
                      <ResponsiveTypography mediumSmall>
                        {t('download_app_info_sportjoe')}
                      </ResponsiveTypography>
                      <img
                        src={qrCodeSportJoe}
                        alt="QR Code"
                        style={{maxWidth: '60%', height: 'auto', marginTop: 10}}
                      />
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          </>
        )}
        {selectedEvent.onDemand && isSmallerScreen && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              width: '100%',
              marginTop: 3,
              marginLeft: 1
            }}
          >
            <Button
              variant="contained"
              color="secondary"
              onClick={handleGoToEvent}
              sx={{
                width: '100%'
              }}
            >
              {t('go_to_event')}
            </Button>
            {isUserAdmin && (
              <>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={onEditButtonClick}
                  sx={{
                    width: '100%'
                  }}
                >
                  {t('edit')}
                </Button>

                <Button
                  variant="contained"
                  color="secondary"
                  onClick={onCloneButtonClick}
                  sx={{
                    width: '100%'
                  }}
                >
                  {t('clone')}
                </Button>

                <Button
                  variant="contained"
                  color="secondary"
                  onClick={onDeleteButtonClick}
                  sx={{
                    width: '100%'
                  }}
                >
                  {t('delete')}
                </Button>
              </>
            )}
          </Box>
        )}

        {tabValue === 1 && (
          <Grid item xs={12} md={12}>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                margin: 'auto'
              }}
            >
              {isSafari ? (
                <SafariMatrixInfo />
              ) : isUserAuthorizedToSeeRoom ? (
                <Chat />
              ) : (
                <NoMemberInfo componentType={'event'} />
              )}
            </Box>
          </Grid>
        )}
      </Grid>
      {selectedUserToOverview && (
        <UserOverview
          userData={selectedUserToOverview}
          userViewDialogOpen={userOverviewDialogOpen}
          onClose={handleUserOverviewClose}
        />
      )}
    </Grid>
  );
};

export default EventInfo;
